import { VerificationStatus, VerificationOverriddenBy } from "shared/types";
import { cx } from "shared/utils";
import { Button, ButtonVariant, ButtonSize, Show } from "shared/components";
import { CursorClick01 } from "untitledui-js-base";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { useMemo } from "react";
import moment from "moment";

const statusMessages = {
  [VerificationStatus.FinishedWithWarnings]: {
    title: "Identity verification passed with warnings",
    message:
      "Applicant has been instructed to contact support to resolve their failed identity verification",
    bgClass: "bg-warning-50",
    iconClass: "text-warning-500",
    textClass: "text-warning-700"
  },
  [VerificationStatus.Failed]: {
    title: "Identity verification failed",
    message:
      "There are issues with identity verification, but basic data has been received and verified",
    bgClass: "bg-danger-50",
    iconClass: "text-danger-500",
    textClass: "text-danger-600"
  },
  [VerificationStatus.ManuallyApproved]: {
    title: "Identity verification manually approved",
    message:
      "Some parts of the identity verification have been manually overridden by our customer support team",
    bgClass: "bg-warning-50",
    iconClass: "text-warning-500",
    textClass: "text-warning-700"
  },
  ["manually_override"]: {
    message:
      "Identity verification has been manually overridden by {name} on {date}.",
    bgClass: "bg-warning-50",
    iconClass: "text-warning-500",
    textClass: "text-warning-700"
  }
};

export const VerificationStatusAlert = ({
  verificationStatus,
  openOverrideIdentityModal,
  overriddenBy
}: {
  verificationStatus: VerificationStatus;
  openOverrideIdentityModal?: () => void;
  overriddenBy: VerificationOverriddenBy | null;
}) => {
  const status = useMemo(() => {
    const baseStatus =
      statusMessages[
        Boolean(overriddenBy) ? "manually_override" : verificationStatus
      ];

    if (overriddenBy)
      baseStatus.message = baseStatus.message
        .replace("{name}", overriddenBy.name)
        .replace(
          "{date}",
          moment(overriddenBy.at).format("MMMM D, YYYY, [at] h:mm A")
        );
    else if (openOverrideIdentityModal)
      baseStatus.message =
        "You can manually override a failed identity verification";

    return baseStatus;
  }, [overriddenBy, verificationStatus, openOverrideIdentityModal]);

  if (!status) return null;

  return (
    <div
      className={cx(
        "px-6 py-4 rounded-lg flex items-center mb-4",
        status.bgClass
      )}
    >
      <InfoIcon className={cx("shrink-0 size-5 ", status.iconClass)} />

      <div
        className={cx(
          "text-sm font-medium flex justify-between w-full pl-4 ml-4 border-l border-alpha-40",
          status.textClass
        )}
      >
        <div>
          <h2 className="font-bold">{status.title}</h2>
          <p className="font-medium">{status.message}</p>
        </div>
        <Show
          if={
            verificationStatus === VerificationStatus.Failed &&
            openOverrideIdentityModal
          }
        >
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            Icon={CursorClick01}
            onClick={openOverrideIdentityModal}
          >
            Override
          </Button>
        </Show>
      </div>
    </div>
  );
};
